import { inject, Injectable } from '@angular/core';
import { getConfiguration } from '@lx/shared/tokens';
import { TabsEnum } from '../utils/tabs-enum';
import { CompanyEnum } from '../models/company.model';
import { SharedDataStore } from '../store';

/**
 * Service to manage the tabs in the Price Lists Portal. Depending on the selected tab, the
 * company will be KIL or ALP.
 *
 * Also, in this service, we check if the junior camps are enabled in the configuration.
 * This is needed because the component we use for displaying the tabs works with indexes, so we cannot use the enum directly.
 *
 * When the junior camps are enabled, the tabs will be:
 * - Kaplan Price Lists
 * - Alpadia Price Lists
 * - Junior Camps Price Lists
 * - Online Courses
 * - Public Holidays
 * When the junior camps are not enabled, the tabs will be:
 * - Kaplan Price Lists
 * - Alpadia Price Lists
 * - Online Courses
 * - Public Holidays
 * - Kaplan Terms and Conditions
 * - Alpadia Terms and Conditions
 * This service is used in the Price Lists Portal component to update the company based on the selected tab index.
 *
 * When the user selects a tab, the `updateCompanyForTab` method is called to update the company in the shared data store.
 *
 * Once the flag for juniors is not needed anymore, this service could be removed or simplified by using only the same tabs as for when the
 * junior camps are enabled.
 *
 * Updated to include AYA Price Lists tab based on feature flags.
 */
@Injectable({
  providedIn: 'root',
})
export class PriceListsTabsService {
  private tabs: Record<number, TabsEnum> = {};

  private readonly tabsByCompany: Record<TabsEnum, CompanyEnum> = {
    [TabsEnum.KAPLAN_PRICELISTS]: CompanyEnum.KIL,
    [TabsEnum.ALPADIA_PRICELISTS]: CompanyEnum.ALP,
    [TabsEnum.JUNIOR_CAMPS_PRICELISTS]: CompanyEnum.ALP,
    [TabsEnum.AYA_PRICELISTS]: CompanyEnum.KIL,
    [TabsEnum.ONLINE_COURSES]: CompanyEnum.KIL,
    [TabsEnum.PUBLIC_HOLIDAYS]: CompanyEnum.KIL,
    [TabsEnum.KAPLAN_TERMS_AND_CONDITIONS]: CompanyEnum.KIL,
    [TabsEnum.ALPADIA_TERMS_AND_CONDITIONS]: CompanyEnum.ALP,
  };
  private readonly configuration = getConfiguration();
  private readonly sharedDataStore = inject(SharedDataStore);

  constructor() {
    this.fillTabsDependingOnFeatureFlags();
  }

  /**
   * Updates the company based on the selected tab index.
   */
  updateCompanyForTab(tabIndex: number): void {
    const selectedTab = this.tabs[tabIndex] || TabsEnum.KAPLAN_PRICELISTS;
    const company = this.tabsByCompany[selectedTab];

    this.sharedDataStore.updateCompany(company);
  }

  private fillTabsDependingOnFeatureFlags(): void {
    const { PRICE_LIST_SHOW_JUNIOR_CAMPS, PRICE_LIST_SHOW_AYA } = this.configuration ?? {};

    const juniorCampsEnabled = !!PRICE_LIST_SHOW_JUNIOR_CAMPS;
    const ayaEnabled = !!PRICE_LIST_SHOW_AYA;

    const tabsList: TabsEnum[] = [TabsEnum.KAPLAN_PRICELISTS, TabsEnum.ALPADIA_PRICELISTS];

    if (ayaEnabled) {
      tabsList.push(TabsEnum.AYA_PRICELISTS);
    }

    if (juniorCampsEnabled) {
      tabsList.push(TabsEnum.JUNIOR_CAMPS_PRICELISTS);
    }

    tabsList.push(TabsEnum.ONLINE_COURSES, TabsEnum.PUBLIC_HOLIDAYS);

    if (!ayaEnabled && !juniorCampsEnabled) {
      tabsList.push(TabsEnum.KAPLAN_TERMS_AND_CONDITIONS, TabsEnum.ALPADIA_TERMS_AND_CONDITIONS);
    }

    this.tabs = tabsList.reduce<Record<number, TabsEnum>>((acc, tab, idx) => {
      acc[idx] = tab;
      return acc;
    }, {});
  }
}
